import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { 
  Download, 
  Trash2, 
  Edit, 
  Shield, 
  AlertCircle, 
  CheckCircle,
  User,
  Calendar,
  FileText,
  Lock,
  Clock
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { gdprService, DataSubjectRequest } from '@/services/gdpr';

type RequestType = 'access' | 'rectification' | 'erasure' | 'portability' | 'restriction' | 'objection' | 'consent_withdrawal';

export function DataSubjectRights() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'rights' | 'requests'>('rights');
  const [selectedRight, setSelectedRight] = useState<RequestType | null>(null);
  const [requestDescription, setRequestDescription] = useState('');
  const [urgencyLevel] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [requests, setRequests] = useState<DataSubjectRequest[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      loadUserRequests();
    }
  }, [user]);

  const loadUserRequests = async () => {
    if (!user) return;
    
    try {
      const userRequests = await gdprService.getDataSubjectRequests(user.id);
      setRequests(userRequests);
    } catch (error) {
      console.error('Error loading requests:', error);
      toast({
        title: "Error",
        description: "Failed to load your data requests",
        variant: "destructive"
      });
    }
  };

  const dataRights = [
    {
      id: 'access' as RequestType,
      title: 'Right to Access',
      description: 'Request a copy of all personal data we hold about you',
      icon: Download,
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      article: 'Article 15'
    },
    {
      id: 'rectification' as RequestType,
      title: 'Right to Rectification',
      description: 'Request correction of inaccurate or incomplete data',
      icon: Edit,
      color: 'bg-green-100 text-green-800 border-green-200',
      article: 'Article 16'
    },
    {
      id: 'erasure' as RequestType,
      title: 'Right to Erasure',
      description: 'Request deletion of your personal data ("right to be forgotten")',
      icon: Trash2,
      color: 'bg-red-100 text-red-800 border-red-200',
      article: 'Article 17'
    },
    {
      id: 'portability' as RequestType,
      title: 'Right to Data Portability',
      description: 'Export your data in a structured, machine-readable format',
      icon: FileText,
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      article: 'Article 20'
    },
    {
      id: 'restriction' as RequestType,
      title: 'Right to Restriction',
      description: 'Request limitation of processing of your personal data',
      icon: Lock,
      color: 'bg-orange-100 text-orange-800 border-orange-200',
      article: 'Article 18'
    },
    {
      id: 'objection' as RequestType,
      title: 'Right to Object',
      description: 'Object to processing based on legitimate interests',
      icon: AlertCircle,
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      article: 'Article 21'
    },
    {
      id: 'consent_withdrawal' as RequestType,
      title: 'Withdraw Consent',
      description: 'Withdraw previously given consent for data processing',
      icon: User,
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      article: 'Article 7(3)'
    }
  ];

  const handleSubmitRequest = async () => {
    if (!selectedRight || !requestDescription.trim() || !user) {
      toast({
        title: "Incomplete Request",
        description: "Please select a right and provide a description.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const requestTypeMap: Record<RequestType, DataSubjectRequest['request_type']> = {
        access: 'data_access',
        rectification: 'data_rectification',
        erasure: 'data_erasure',
        portability: 'data_portability',
        restriction: 'processing_restriction',
        objection: 'objection_processing',
        consent_withdrawal: 'consent_withdrawal'
      };

      await gdprService.submitDataSubjectRequest({
        user_id: user.id,
        request_type: requestTypeMap[selectedRight],
        description: requestDescription,
        urgency_level: urgencyLevel
      });

      toast({
        title: "Request Submitted",
        description: `Your ${dataRights.find(r => r.id === selectedRight)?.title} request has been submitted. We'll respond within 30 days.`,
        variant: "default"
      });

      // Reset form and reload requests
      setSelectedRight(null);
      setRequestDescription('');
      setActiveTab('requests');
      await loadUserRequests();
      
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDataExport = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to export your data.",
        variant: "destructive"
      });
      return;
    }

    setIsExporting(true);
    
    try {
      await gdprService.downloadUserDataExport();

      toast({
        title: "Data Export Complete",
        description: "Your personal data has been downloaded successfully.",
        variant: "default"
      });

    } catch (error) {
      console.error('Error exporting data:', error);
      toast({
        title: "Export Failed",
        description: "There was an error exporting your data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleAccountDeletion = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to delete your account.",
        variant: "destructive"
      });
      return;
    }

    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data.'
    );

    if (!confirmed) return;

    setIsSubmitting(true);
    
    try {
      // Create account deletion request
      await gdprService.submitDataSubjectRequest({
        user_id: user.id,
        request_type: 'data_erasure',
        description: 'Complete account deletion requested by user',
        urgency_level: 'high'
      });

      toast({
        title: "Deletion Request Submitted",
        description: "Your account deletion request has been submitted. We'll process it within 30 days.",
        variant: "default"
      });

      // Reload requests and switch to requests tab
      await loadUserRequests();
      setActiveTab('requests');

    } catch (error) {
      console.error('Error submitting deletion request:', error);
      toast({
        title: "Deletion Request Failed",
        description: "There was an error submitting your deletion request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="space-y-6">
        <div className="text-center space-y-4">
          <Badge variant="outline" className="mb-4">
            <Shield className="h-4 w-4 mr-2" />
            GDPR Data Rights
          </Badge>
          <h1 className="text-3xl font-bold">Data Subject Rights</h1>
          <p className="text-muted-foreground">
            Exercise your rights under the General Data Protection Regulation (GDPR)
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center space-x-4 mb-6">
          <Button
            variant={activeTab === 'rights' ? 'default' : 'outline'}
            onClick={() => setActiveTab('rights')}
          >
            Your Rights
          </Button>
          <Button
            variant={activeTab === 'requests' ? 'default' : 'outline'}
            onClick={() => setActiveTab('requests')}
          >
            My Requests
          </Button>
        </div>

        {activeTab === 'rights' && (
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Button
                    onClick={handleDataExport}
                    disabled={isSubmitting || isExporting}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    {isExporting ? 'Exporting...' : 'Export My Data'}
                  </Button>
                  <Button
                    onClick={handleAccountDeletion}
                    disabled={isSubmitting}
                    variant="destructive"
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete My Account
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Data Rights Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dataRights.map((right) => {
                const Icon = right.icon;
                return (
                  <Card
                    key={right.id}
                    className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedRight === right.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => setSelectedRight(right.id)}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <Icon className="h-6 w-6 text-blue-600" />
                        <Badge variant="outline" className="text-xs">
                          {right.article}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg">{right.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        {right.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Request Form */}
            {selectedRight && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    Submit {dataRights.find(r => r.id === selectedRight)?.title} Request
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Request Details
                    </label>
                    <Textarea
                      value={requestDescription}
                      onChange={(e) => setRequestDescription(e.target.value)}
                      placeholder="Please provide specific details about your request..."
                      rows={4}
                    />
                  </div>

                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      We will respond to your request within 30 days as required by GDPR.
                      For urgent requests, please contact our DPO at dpo@topmatchtalent.com.
                    </AlertDescription>
                  </Alert>

                  <div className="flex gap-3">
                    <Button
                      onClick={handleSubmitRequest}
                      disabled={isSubmitting || !requestDescription.trim()}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setSelectedRight(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'requests' && (
          <Card>
            <CardHeader>
              <CardTitle>Your Data Subject Rights Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {requests.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No requests submitted yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map((request) => {
                    // Map request_type to readable format
                    const getRequestTitle = (requestType: string) => {
                      const typeMap: Record<string, string> = {
                        'data_access': 'Right to Access',
                        'data_rectification': 'Right to Rectification', 
                        'data_erasure': 'Right to Erasure',
                        'data_portability': 'Right to Data Portability',
                        'processing_restriction': 'Right to Restriction',
                        'consent_withdrawal': 'Withdraw Consent',
                        'objection_processing': 'Right to Object',
                        'automated_decision_objection': 'Objection to Automated Decision-Making'
                      };
                      return typeMap[requestType] || requestType;
                    };

                    return (
                      <div
                        key={request.id}
                        className="border rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">
                              {getRequestTitle(request.request_type)}
                            </Badge>
                            <Badge 
                              variant={request.status === 'completed' ? 'default' : 'secondary'}
                            >
                              {request.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            {request.created_at ? new Date(request.created_at).toLocaleDateString() : 'Unknown date'}
                          </div>
                        </div>
                        <p className="text-sm">{request.description || 'No description provided'}</p>
                        {request.resolution_notes && (
                          <div className="bg-green-50 p-3 rounded-md">
                            <p className="text-sm font-medium text-green-800 mb-1">
                              Response:
                            </p>
                            <p className="text-sm text-green-700">{request.resolution_notes}</p>
                          </div>
                        )}
                        {request.status === 'pending' && (
                          <div className="flex items-center gap-2 text-sm text-orange-600">
                            <Clock className="h-4 w-4" />
                            Awaiting response (within 30 days)
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
} 